import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Heart, Info, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { PageHeader } from '../shared/PageHeader';
import { useAppStore } from '../../hooks/useAppStore';
import { getLinkedLearners } from '../../utils/accountHelpers';
import { isParentAccount } from '../../types/accounts';
import { ChildSelector } from './ChildSelector';
import { ChildSummaryCard } from './ChildSummaryCard';
import { SubjectProgressGrid } from './SubjectProgressGrid';
import { ParentTips } from './ParentTips';
import { WeeklyHighlights } from './WeeklyHighlights';
import { RecentActivityList } from './RecentActivityList';
import { AchievementCard } from './AchievementCard';
import { getWeeklyStats, getRecentSessionsForLearner, getRecentMilestones } from '../../utils/activityHelpers';
interface ParentPortalPageProps {
  onNavigateToLearner?: (learnerId: string) => void;
}
export function ParentPortalPage({
  onNavigateToLearner
}: ParentPortalPageProps) {
  const {
    accounts,
    activeAccount,
    selectedGroup,
    getLearnerData,
    getLearnerModuleMastery,
    isInitialized,
    setViewingLearner,
    viewingLearnerId
  } = useAppStore();
  const isParent = activeAccount && isParentAccount(activeAccount);
  if (!isInitialized) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-4119">
        <div className="text-center" data-id="element-4120">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" data-id="element-4121" />
          <p className="text-slate-500 font-medium" data-id="element-4122">Laden...</p>
        </div>
      </div>;
  }
  if (!isParent) {
    return <div className="max-w-2xl mx-auto" data-id="element-4123">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-4124">
          <Card className="p-12 text-center border-2 border-emerald-100 bg-emerald-50/30" data-id="element-4125">
            <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 border-4 border-emerald-100" data-id="element-4126">
              <Users className="w-10 h-10 text-emerald-600" data-id="element-4127" />
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-4128">
              Ouderportaal
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed max-w-md mx-auto" data-id="element-4129">
              Dit portaal is bedoeld voor ouders om de voortgang van hun
              kinderen te bekijken.
            </p>
            <div className="bg-white rounded-xl p-6 border-2 border-emerald-200 max-w-md mx-auto" data-id="element-4130">
              <div className="flex items-start gap-3 text-left" data-id="element-4131">
                <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-4132" />
                <div className="text-sm text-slate-700" data-id="element-4133">
                  <p className="font-bold mb-2" data-id="element-4134">Account wisselen</p>
                  <p className="text-slate-600" data-id="element-4135">
                    Je bent nu ingelogd als een ander accounttype. Gebruik de
                    account-switcher rechtsboven om naar je ouderaccount te
                    wisselen.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>;
  }
  const linkedLearners = getLinkedLearners(accounts, activeAccount.id);
  // Auto-select first child if none selected and there is only 1
  if (!viewingLearnerId && linkedLearners.length === 1) {
    setViewingLearner(linkedLearners[0].id);
  }
  const selectedChild = viewingLearnerId ? linkedLearners.find(l => l.id === viewingLearnerId) : linkedLearners[0];
  const childData = selectedChild ? getLearnerData(selectedChild.id) : null;
  const weeklyStats = childData ? getWeeklyStats(childData.questionStates || {}) : null;
  const recentSessions = childData ? getRecentSessionsForLearner(childData.questionStates || {}, 5) : [];
  const milestones = childData ? getRecentMilestones(childData, selectedGroup) : [];
  return <div className="pb-20 space-y-8" data-id="element-4136">
      <PageHeader icon={Heart} title="Mijn Kinderen" subtitle="Bekijk de voortgang en moedig aan" accentColor="bg-emerald-100 text-emerald-600" data-id="element-4137" />

      {linkedLearners.length === 0 ? <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-4138">
          <Card className="p-12 text-center border-2 border-amber-100 bg-amber-50/30" data-id="element-4139">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 border-4 border-amber-100" data-id="element-4140">
              <AlertCircle className="w-8 h-8 text-amber-600" data-id="element-4141" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-3" data-id="element-4142">
              Geen gekoppelde kinderen
            </h3>
            <p className="text-slate-600 text-lg max-w-md mx-auto" data-id="element-4143">
              Er zijn nog geen leerling-accounts gekoppeld aan dit
              ouder-account.
            </p>
          </Card>
        </motion.div> : <>
          {linkedLearners.length > 1 && <ChildSelector childrenList={linkedLearners.map(l => ({
        id: l.id,
        name: l.displayName
      }))} selectedChildId={selectedChild?.id || ''} onSelectChild={setViewingLearner} data-id="element-4144" />}

          <AnimatePresence mode="wait" data-id="element-4145">
            {selectedChild && childData && <motion.div key={selectedChild.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-4146">
                <AchievementCard milestones={milestones} data-id="element-4147" />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4148">
                  <div className="lg:col-span-1 space-y-8" data-id="element-4149">
                    <ChildSummaryCard childName={selectedChild.displayName} learnerData={childData} selectedGroup={selectedGroup} getModuleMastery={moduleId => getLearnerModuleMastery(selectedChild.id, moduleId)} onViewDashboard={onNavigateToLearner ? () => onNavigateToLearner(selectedChild.id) : undefined} data-id="element-4150" />
                    {weeklyStats && <WeeklyHighlights stats={weeklyStats} data-id="element-4151" />}
                    <ParentTips childName={selectedChild.displayName} learnerData={childData} selectedGroup={selectedGroup} getModuleMastery={moduleId => getLearnerModuleMastery(selectedChild.id, moduleId)} data-id="element-4152" />
                  </div>
                  <div className="lg:col-span-2 space-y-8" data-id="element-4153">
                    <SubjectProgressGrid learnerData={childData} selectedGroup={selectedGroup} getModuleMastery={moduleId => getLearnerModuleMastery(selectedChild.id, moduleId)} data-id="element-4154" />
                    <RecentActivityList sessions={recentSessions} data-id="element-4155" />
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </>}
    </div>;
}